import { useEffect, useState } from "react";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import {
  clearStoredCustomOrderOwnerSessionToken,
  CustomOrderAPI,
  storeCustomOrderOwnerSessionToken,
} from "../../lib/api";
import {
  CASE_VIEW_PANEL_HEADING_CLASS,
  CASE_VIEW_SUPPORTING_READING_CLASS,
} from "../../utils/caseViewTypography";
import { getLocalizedCustomCaseApiMessage } from "../../utils/customCaseApiErrors";
import { devError, devLog } from "../../utils/devLog";

const OWNER_ACCESS_DEFAULT_COPY = {
  en: {
    title: "Verify Your Email",
    description:
      "This custom order is protected. We will send a 6-digit access code to the order email before opening the private case.",
    loading: "Checking private access...",
    badge: "Private Owner Access",
    sendCodeToOrderEmail: "Send the code to the order email",
    rememberedNotice:
      "We only ask for this on a new browser or device. After verification, this browser stays remembered for future visits.",
    requestIdle: "Email Access Code",
    requestBusy: "Sending Code...",
    codeLabel: "6-digit code",
    codePlaceholder: "123456",
    verifyIdle: "Verify And Continue",
    verifyBusy: "Verifying...",
    clearRememberedAccess: "Clear Remembered Access",
    devCodePreview: "Dev code preview",
    invalidCode: "Enter the 6-digit code from your email.",
    statusCodeSent: "Access code sent. Enter it below to continue.",
    statusVerified: "Access confirmed for this browser.",
    statusCleared: "Remembered access cleared for this browser.",
    loadAccessStateError: "Unable to load access state.",
    requestCodeError: "Unable to send access code.",
    verifyCodeError: "Unable to verify access code.",
    clearAccessError: "Unable to clear remembered access.",
    invalidOwnerEmailError: "A valid order email is required before we can send a code.",
    ownerEmailMismatchError: "This email does not match the order owner.",
    requestRateLimitedError: "Please wait a moment before requesting another code.",
    expiredCodeError: "This access code has expired. Request a new one.",
    incorrectCodeError: "The access code is incorrect.",
    paymentRequiredError: "Complete payment before opening this custom order.",
    notFoundError: "Custom order not found.",
  },
  fr: {
    title: "Verifiez votre email",
    description:
      "Cette commande personnalisee est protegee. Nous enverrons un code d'acces a 6 chiffres a l'email de la commande avant d'ouvrir le dossier prive.",
    loading: "Verification de l'acces prive...",
    badge: "Acces proprietaire prive",
    sendCodeToOrderEmail: "Envoyer le code a l'email de la commande",
    rememberedNotice:
      "Nous ne le demandons que sur un nouveau navigateur ou appareil. Une fois verifie, ce navigateur reste memorise pour les prochaines visites.",
    requestIdle: "Envoyer le code d'acces",
    requestBusy: "Envoi du code...",
    codeLabel: "Code a 6 chiffres",
    codePlaceholder: "123456",
    verifyIdle: "Verifier et continuer",
    verifyBusy: "Verification...",
    clearRememberedAccess: "Effacer l'acces memorise",
    devCodePreview: "Code dev",
    invalidCode: "Entrez le code a 6 chiffres recu par email.",
    statusCodeSent: "Code envoye. Entrez-le ci-dessous pour continuer.",
    statusVerified: "Acces confirme pour ce navigateur.",
    statusCleared: "Acces memorise efface pour ce navigateur.",
    loadAccessStateError: "Impossible de charger l'etat d'acces.",
    requestCodeError: "Impossible d'envoyer le code d'acces.",
    verifyCodeError: "Impossible de verifier le code d'acces.",
    clearAccessError: "Impossible d'effacer l'acces memorise.",
    invalidOwnerEmailError: "Un email de commande valide est requis avant d'envoyer un code.",
    ownerEmailMismatchError: "Cet email ne correspond pas au proprietaire de la commande.",
    requestRateLimitedError: "Patientez un instant avant de demander un nouveau code.",
    expiredCodeError: "Ce code d'acces a expire. Demandez-en un nouveau.",
    incorrectCodeError: "Le code d'acces est incorrect.",
    paymentRequiredError: "Terminez le paiement avant d'ouvrir cette commande personnalisee.",
    notFoundError: "Commande personnalisee introuvable.",
  },
  es: {
    title: "Verifica tu correo",
    description:
      "Este pedido personalizado esta protegido. Enviaremos un codigo de acceso de 6 digitos al correo del pedido antes de abrir el caso privado.",
    loading: "Comprobando el acceso privado...",
    badge: "Acceso privado del propietario",
    sendCodeToOrderEmail: "Enviar el codigo al correo del pedido",
    rememberedNotice:
      "Solo lo pedimos en un navegador o dispositivo nuevo. Despues de verificarlo, este navegador quedara recordado para futuras visitas.",
    requestIdle: "Enviar codigo de acceso",
    requestBusy: "Enviando codigo...",
    codeLabel: "Codigo de 6 digitos",
    codePlaceholder: "123456",
    verifyIdle: "Verificar y continuar",
    verifyBusy: "Verificando...",
    clearRememberedAccess: "Borrar acceso recordado",
    devCodePreview: "Codigo dev",
    invalidCode: "Introduce el codigo de 6 digitos de tu correo.",
    statusCodeSent: "Codigo enviado. Introducelo abajo para continuar.",
    statusVerified: "Acceso confirmado para este navegador.",
    statusCleared: "Acceso recordado borrado para este navegador.",
    loadAccessStateError: "No se pudo cargar el estado de acceso.",
    requestCodeError: "No se pudo enviar el codigo de acceso.",
    verifyCodeError: "No se pudo verificar el codigo de acceso.",
    clearAccessError: "No se pudo borrar el acceso recordado.",
    invalidOwnerEmailError: "Se necesita un correo valido del pedido antes de enviar un codigo.",
    ownerEmailMismatchError: "Este correo no coincide con el propietario del pedido.",
    requestRateLimitedError: "Espera un momento antes de solicitar otro codigo.",
    expiredCodeError: "Este codigo de acceso ha caducado. Solicita uno nuevo.",
    incorrectCodeError: "El codigo de acceso es incorrecto.",
    paymentRequiredError: "Completa el pago antes de abrir este pedido personalizado.",
    notFoundError: "Pedido personalizado no encontrado.",
  },
  de: {
    title: "Bestatigen Sie Ihre E-Mail",
    description:
      "Diese individuelle Bestellung ist geschutzt. Bevor wir den privaten Fall offnen, senden wir einen 6-stelligen Zugangscode an die Bestell-E-Mail.",
    loading: "Privaten Zugriff prufen...",
    badge: "Privater Eigentumerzugang",
    sendCodeToOrderEmail: "Code an die Bestell-E-Mail senden",
    rememberedNotice:
      "Wir fragen nur in einem neuen Browser oder auf einem neuen Gerat danach. Nach der Bestatigung bleibt dieser Browser fur spatere Besuche gespeichert.",
    requestIdle: "Zugangscode senden",
    requestBusy: "Code wird gesendet...",
    codeLabel: "6-stelliger Code",
    codePlaceholder: "123456",
    verifyIdle: "Bestatigen und fortfahren",
    verifyBusy: "Wird bestatigt...",
    clearRememberedAccess: "Gespeicherten Zugriff loschen",
    devCodePreview: "Dev-Code",
    invalidCode: "Geben Sie den 6-stelligen Code aus Ihrer E-Mail ein.",
    statusCodeSent: "Zugangscode gesendet. Geben Sie ihn unten ein, um fortzufahren.",
    statusVerified: "Zugriff fur diesen Browser bestatigt.",
    statusCleared: "Gespeicherter Zugriff fur diesen Browser geloscht.",
    loadAccessStateError: "Zugriffsstatus konnte nicht geladen werden.",
    requestCodeError: "Zugangscode konnte nicht gesendet werden.",
    verifyCodeError: "Zugangscode konnte nicht bestatigt werden.",
    clearAccessError: "Gespeicherter Zugriff konnte nicht geloscht werden.",
    invalidOwnerEmailError: "Bevor wir einen Code senden, wird eine gultige Bestell-E-Mail benotigt.",
    ownerEmailMismatchError: "Diese E-Mail stimmt nicht mit dem Bestellinhaber uberein.",
    requestRateLimitedError: "Bitte warten Sie einen Moment, bevor Sie einen neuen Code anfordern.",
    expiredCodeError: "Dieser Zugangscode ist abgelaufen. Fordern Sie einen neuen an.",
    incorrectCodeError: "Der Zugangscode ist falsch.",
    paymentRequiredError: "Schliessen Sie die Zahlung ab, bevor Sie diese individuelle Bestellung offnen.",
    notFoundError: "Individuelle Bestellung nicht gefunden.",
  },
  it: {
    title: "Verifica la tua email",
    description:
      "Questo ordine personalizzato e protetto. Invieremo un codice di accesso di 6 cifre all'email dell'ordine prima di aprire il caso privato.",
    loading: "Verifica dell'accesso privato...",
    badge: "Accesso privato del proprietario",
    sendCodeToOrderEmail: "Invia il codice all'email dell'ordine",
    rememberedNotice:
      "Lo chiediamo solo su un nuovo browser o dispositivo. Dopo la verifica, questo browser restera memorizzato per le visite future.",
    requestIdle: "Invia il codice di accesso",
    requestBusy: "Invio del codice...",
    codeLabel: "Codice di 6 cifre",
    codePlaceholder: "123456",
    verifyIdle: "Verifica e continua",
    verifyBusy: "Verifica in corso...",
    clearRememberedAccess: "Cancella accesso memorizzato",
    devCodePreview: "Codice dev",
    invalidCode: "Inserisci il codice di 6 cifre ricevuto via email.",
    statusCodeSent: "Codice inviato. Inseriscilo qui sotto per continuare.",
    statusVerified: "Accesso confermato per questo browser.",
    statusCleared: "Accesso memorizzato cancellato per questo browser.",
    loadAccessStateError: "Impossibile caricare lo stato di accesso.",
    requestCodeError: "Impossibile inviare il codice di accesso.",
    verifyCodeError: "Impossibile verificare il codice di accesso.",
    clearAccessError: "Impossibile cancellare l'accesso memorizzato.",
    invalidOwnerEmailError: "Serve un'email ordine valida prima di inviare un codice.",
    ownerEmailMismatchError: "Questa email non corrisponde al proprietario dell'ordine.",
    requestRateLimitedError: "Attendi un momento prima di richiedere un nuovo codice.",
    expiredCodeError: "Questo codice di accesso e scaduto. Richiedine uno nuovo.",
    incorrectCodeError: "Il codice di accesso non e corretto.",
    paymentRequiredError: "Completa il pagamento prima di aprire questo ordine personalizzato.",
    notFoundError: "Ordine personalizzato non trovato.",
  },
} as const;

type OwnerAccessLocale = keyof typeof OWNER_ACCESS_DEFAULT_COPY;

const resolveOwnerAccessLocale = (value = ""): OwnerAccessLocale => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "fr") return "fr";
  if (normalized === "es") return "es";
  if (normalized === "de") return "de";
  if (normalized === "it") return "it";
  return "en";
};

type CustomOrderOwnerAccessGateProps = {
  accessToken: string;
  routeLabel: string;
  locale?: string;
  title?: string;
  description?: string;
  copy?: Partial<{
    loading: string;
    badge: string;
    sendCodeToOrderEmail: string;
    rememberedNotice: string;
    requestIdle: string;
    requestBusy: string;
    codeLabel: string;
    codePlaceholder: string;
    verifyIdle: string;
    verifyBusy: string;
    clearRememberedAccess: string;
    devCodePreview: string;
    invalidCode: string;
    statusCodeSent: string;
    statusVerified: string;
    statusCleared: string;
    loadAccessStateError: string;
    requestCodeError: string;
    verifyCodeError: string;
    clearAccessError: string;
    invalidOwnerEmailError: string;
    ownerEmailMismatchError: string;
    requestRateLimitedError: string;
    expiredCodeError: string;
    incorrectCodeError: string;
    paymentRequiredError: string;
    notFoundError: string;
  }>;
  onVerified?: () => void;
  onAccessStateResolved?: (payload: {
    ownerVerified: boolean;
    maskedEmail: string;
    marketingOptIn: boolean;
    policyAcknowledgedAt?: string | null;
    status: string;
    shareLinks: Array<{
      id: string;
      label: string;
      mode: string;
      expiresAt?: string | null;
      createdAt?: string | null;
      accessCount: number;
      sharePath: string;
      shareUrl: string;
    }>;
  }) => void;
  children: React.ReactNode;
};

export default function CustomOrderOwnerAccessGate({
  accessToken,
  routeLabel,
  locale = "en",
  title,
  description,
  copy = {},
  onVerified,
  onAccessStateResolved,
  children,
}: CustomOrderOwnerAccessGateProps) {
  const baseCopy = OWNER_ACCESS_DEFAULT_COPY[resolveOwnerAccessLocale(locale)];
  const resolvedCopy = {
    ...baseCopy,
    ...copy,
  };
  const resolvedTitle = title || baseCopy.title;
  const resolvedDescription = description || baseCopy.description;
  const [isLoadingAccess, setIsLoadingAccess] = useState(true);
  const [ownerVerified, setOwnerVerified] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [devCodePreview, setDevCodePreview] = useState("");
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  const resolveLocalizedErrorMessage = (err: any, fallback: string) => {
    const responseCode = String(err?.response?.data?.code || "").trim();
    if (responseCode === "custom_order_not_found") {
      return resolvedCopy.notFoundError;
    }

    const responseMessage = String(
      err?.response?.data?.message || err?.response?.data?.error || "",
    ).trim();
    if (responseMessage === "Custom order not found" || responseMessage === "Custom order not found.") {
      return resolvedCopy.notFoundError;
    }
    if (responseMessage.startsWith("Complete payment before you can ")) {
      return resolvedCopy.paymentRequiredError;
    }

    return getLocalizedCustomCaseApiMessage({
      error: err,
      locale,
      fallback,
    });
  };

  const loadAccessState = async (source: string) => {
    if (!accessToken) return;
    try {
      setIsLoadingAccess(true);
      setError("");
      devLog("route", "custom_order_owner_access_state_load_started", {
        route: routeLabel,
        source,
        hasAccessToken: Boolean(accessToken),
      });
      const response = await CustomOrderAPI.getAccessState(accessToken);
      const nextAccess = response?.access;
      setOwnerVerified(Boolean(nextAccess?.ownerVerified));
      setMaskedEmail(String(nextAccess?.maskedEmail || "").trim());
      onAccessStateResolved?.({
        ownerVerified: Boolean(nextAccess?.ownerVerified),
        maskedEmail: String(nextAccess?.maskedEmail || "").trim(),
        marketingOptIn: Boolean(nextAccess?.marketingOptIn),
        policyAcknowledgedAt: nextAccess?.policyAcknowledgedAt || null,
        status: String(nextAccess?.status || "").trim(),
        shareLinks: Array.isArray(nextAccess?.shareLinks)
          ? nextAccess.shareLinks
          : [],
      });
      devLog("route", "custom_order_owner_access_state_load_succeeded", {
        route: routeLabel,
        source,
        ownerVerified: Boolean(nextAccess?.ownerVerified),
        hasMaskedEmail: Boolean(nextAccess?.maskedEmail),
      });
    } catch (err: any) {
      const nextMessage = resolveLocalizedErrorMessage(
        err,
        resolvedCopy.loadAccessStateError,
      );
      setError(nextMessage);
      devError("route", "custom_order_owner_access_state_load_failed", {
        route: routeLabel,
        source,
        message: nextMessage,
        status: err?.response?.status,
      });
    } finally {
      setIsLoadingAccess(false);
    }
  };

  useEffect(() => {
    void loadAccessState("mount");
  }, [accessToken]);

  useEffect(() => {
    if (!ownerVerified) return;
    onVerified?.();
  }, [ownerVerified, onVerified]);

  const handleRequestCode = async () => {
    if (!accessToken) return;
    try {
      setIsRequestingCode(true);
      setError("");
      setStatus("");
      devLog("route", "custom_order_owner_access_code_request_started", {
        route: routeLabel,
        hasAccessToken: Boolean(accessToken),
      });
      const response = await CustomOrderAPI.requestAccessCode({ accessToken });
      setMaskedEmail(String(response?.access?.maskedEmail || "").trim());
      setDevCodePreview(String(response?.access?.devCodePreview || "").trim());
      setStatus(resolvedCopy.statusCodeSent);
      devLog("route", "custom_order_owner_access_code_request_succeeded", {
        route: routeLabel,
        hasMaskedEmail: Boolean(response?.access?.maskedEmail),
        hasDevCodePreview: Boolean(response?.access?.devCodePreview),
      });
    } catch (err: any) {
      const nextMessage = resolveLocalizedErrorMessage(
        err,
        resolvedCopy.requestCodeError,
      );
      setError(nextMessage);
      devError("route", "custom_order_owner_access_code_request_failed", {
        route: routeLabel,
        message: nextMessage,
        status: err?.response?.status,
      });
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!accessToken) return;
    if (!/^\d{6}$/.test(String(accessCode || "").trim())) {
      setError(resolvedCopy.invalidCode);
      return;
    }

    try {
      setIsVerifyingCode(true);
      setError("");
      setStatus("");
      devLog("route", "custom_order_owner_access_code_verify_started", {
        route: routeLabel,
        hasAccessToken: Boolean(accessToken),
      });
      const response = await CustomOrderAPI.verifyAccessCode({
        accessToken,
        code: String(accessCode || "").trim(),
      });
      storeCustomOrderOwnerSessionToken(
        String(response?.access?.accessToken || accessToken || "").trim(),
        String(response?.access?.sessionToken || "").trim(),
      );
      setOwnerVerified(true);
      setStatus(resolvedCopy.statusVerified);
      devLog("route", "custom_order_owner_access_code_verify_succeeded", {
        route: routeLabel,
        hasAccessToken: Boolean(accessToken),
        hasSessionToken: Boolean(
          String(response?.access?.sessionToken || "").trim(),
        ),
      });
      void loadAccessState("post_verify");
    } catch (err: any) {
      const nextMessage = resolveLocalizedErrorMessage(
        err,
        resolvedCopy.verifyCodeError,
      );
      setError(nextMessage);
      devError("route", "custom_order_owner_access_code_verify_failed", {
        route: routeLabel,
        message: nextMessage,
        status: err?.response?.status,
      });
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleClearRememberedAccess = async () => {
    if (!accessToken) return;
    try {
      await CustomOrderAPI.clearAccessSession(accessToken);
      clearStoredCustomOrderOwnerSessionToken(accessToken);
      setOwnerVerified(false);
      setAccessCode("");
      setStatus(resolvedCopy.statusCleared);
      await loadAccessState("cleared_session");
    } catch (err: any) {
      const nextMessage = resolveLocalizedErrorMessage(
        err,
        resolvedCopy.clearAccessError,
      );
      setError(nextMessage);
    }
  };

  if (isLoadingAccess) {
    return (
      <div className="flex min-h-[280px] items-center justify-center">
        <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/75">
          <Loader2 className="h-4 w-4 animate-spin" />
          {resolvedCopy.loading}
        </div>
      </div>
    );
  }

  if (!ownerVerified) {
    return (
      <div className="rounded-[32px] border border-white/10 bg-[linear-gradient(135deg,rgba(11,16,28,0.98),rgba(24,10,16,0.95))] p-6 text-white shadow-[0_25px_70px_rgba(0,0,0,0.36)] sm:p-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6b26b]/25 bg-[#d6b26b]/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-[#eed7a3]">
          <ShieldCheck className="h-3.5 w-3.5" />
          {resolvedCopy.badge}
        </div>
        <h2 className={`font-heading ${CASE_VIEW_PANEL_HEADING_CLASS}`}>
          {resolvedTitle}
        </h2>
        <p
          className={`mt-3 max-w-2xl text-white/72 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}
        >
          {resolvedDescription}
        </p>

        <div className="mt-6 rounded-[24px] border border-white/10 bg-black/20 p-4">
          <div className="flex items-start gap-3">
            <Mail className="mt-1 h-5 w-5 text-[#d6b26b]" />
            <div>
              <p className="text-sm font-semibold text-white">
                {maskedEmail
                  ? `${resolvedCopy.sendCodeToOrderEmail.replace(/\.$/, "")} ${maskedEmail}`
                  : resolvedCopy.sendCodeToOrderEmail}
              </p>
              <p
                className={`mt-2 text-white/68 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}
              >
                {resolvedCopy.rememberedNotice}
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleRequestCode}
              disabled={isRequestingCode}
              className="inline-flex items-center justify-center rounded-full bg-[#d7263d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#f23c56] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRequestingCode
                ? resolvedCopy.requestBusy
                : resolvedCopy.requestIdle}
            </button>
          </div>

          <div className="mt-5">
            <label className="text-sm font-semibold text-white">
              {resolvedCopy.codeLabel}
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={accessCode}
              onChange={(event) =>
                setAccessCode(event.target.value.replace(/\D/g, "").slice(0, 6))
              }
              className="mt-3 w-full rounded-[18px] border border-white/10 bg-white/5 px-4 py-3 text-center text-lg tracking-[0.34em] text-white outline-none transition placeholder:text-white/30 focus:border-[#d7263d] focus:ring-2 focus:ring-[#d7263d]/25"
              placeholder={resolvedCopy.codePlaceholder}
            />
            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={isVerifyingCode}
              className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-white/12 bg-white/6 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isVerifyingCode
                ? resolvedCopy.verifyBusy
                : resolvedCopy.verifyIdle}
            </button>
          </div>

          {status ? (
            <p className="mt-4 rounded-2xl border border-emerald-300/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
              {status}
            </p>
          ) : null}
          {error ? (
            <p className="mt-4 rounded-2xl border border-rose-300/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
              {error}
            </p>
          ) : null}
          {devCodePreview ? (
            <p className="mt-4 text-xs text-white/52">
              {resolvedCopy.devCodePreview}:{" "}
              <span className="font-semibold tracking-[0.2em] text-white/78">
                {devCodePreview}
              </span>
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={handleClearRememberedAccess}
          className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          {resolvedCopy.clearRememberedAccess}
        </button>
      </div>
      {children}
    </>
  );
}
